import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { datadogRum } from "@datadog/browser-rum";
import WindmillLoading from "../components/Loading/Loading";
import { getAuthToken, getUserInfo } from "../services/authService-sighup";
import { useChildApps } from "./ChildAppsContext";
import { AppBlockedError, AppContractError, AppLinkExpiredError, loadAppWithRetry } from "./loadApp";
import { childRoute, createChildHost } from "./host";

type MountState = { phase: "loading" } | { phase: "ready" } | { phase: "error"; message: string };

const messageFor = (error: unknown) => {
  if (error instanceof AppLinkExpiredError) return "This app's link expired. Reload the page to try again.";
  if (error instanceof AppBlockedError) return "This app is not served from an approved location.";
  if (error instanceof AppContractError) return "This app is not compatible with this version of AMP.";
  return "This app could not be downloaded.";
};

// Mounts one child app into a plain container, like the AI shell does: the
// child owns everything inside it (including its own Shadow DOM and styles).
// The app is mounted once per slug; later route changes reach it through the
// host's subscribeRoute and never remount it.
const ChildAppMount: React.FC<{ slug: string }> = ({ slug }) => {
  const { apps, status, refresh } = useChildApps();
  const location = useLocation();
  const navigate = useNavigate();
  // useNavigate returns a new function whenever the location changes. Reading it
  // through a ref keeps it out of the mount effect's dependencies, so a route
  // change moves the child but never unmounts it.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const containerRef = useRef<HTMLDivElement>(null);
  const notifyRouteRef = useRef<((route: string) => void) | null>(null);
  const [state, setState] = useState<MountState>({ phase: "loading" });
  const [attempt, setAttempt] = useState(0);

  const app = apps.find((candidate) => candidate.slug === slug);
  const hasApp = Boolean(app);
  const appRef = useRef(app);
  appRef.current = app;
  const routeRef = useRef("/");
  routeRef.current = childRoute(location.pathname, slug) + location.search;

  useEffect(() => {
    const current = appRef.current;
    const element = containerRef.current;
    if (!current || !element) return;
    let cancelled = false;
    let handle: { unmount(): void } | null = null;
    setState({ phase: "loading" });

    loadAppWithRetry(current, () => refresh(slug))
      .then((module) => {
        if (cancelled) return;
        const info = getUserInfo(false);
        const { host, notifyRoute } = createChildHost({
          app: current,
          user: {
            id: info?.localAccountId ?? "",
            name: info?.fullName ?? "",
            email: info?.email ?? "",
          },
          initialRoute: routeRef.current,
          navigate: (path, opts) => navigateRef.current(path, opts),
          getToken: async (scopes) => {
            const token = await getAuthToken(scopes?.[0]);
            if (!token) throw new Error("No access token is available.");
            return token;
          },
          telemetry: {
            event: (name, props) => datadogRum.addAction(name, props),
            error: (error, context) => datadogRum.addError(error, context),
          },
          apimSubscriptionKey: process.env.REACT_APP_CHILD_APPS_APIM_KEY || undefined,
          product: process.env.REACT_APP_PRODUCT_APIM || undefined,
        });
        notifyRouteRef.current = notifyRoute;
        handle = module.mount(element, host);
        datadogRum.addAction("child_app_opened", { slug, version: module.meta.version });
        setState({ phase: "ready" });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(`Child app ${slug} failed to load`, error);
        datadogRum.addError(error, { slug });
        setState({ phase: "error", message: messageFor(error) });
      });

    return () => {
      cancelled = true;
      notifyRouteRef.current = null;
      try {
        handle?.unmount();
      } catch (error) {
        console.error(`Child app ${slug} threw while unmounting`, error);
      }
      element.replaceChildren();
    };
    // refresh is stable; the app and navigate are read through refs so a
    // refreshed list (new signed links) or a route change never remounts a running app.
  }, [slug, hasApp, attempt, refresh]);

  useEffect(() => {
    notifyRouteRef.current?.(routeRef.current);
  }, [location.pathname, location.search]);

  if (status === "idle" || status === "loading") return <WindmillLoading />;
  if (!app) return <Navigate to="/unauthorized" replace />;

  return (
    <>
      {state.phase === "loading" && <WindmillLoading />}
      {state.phase === "error" && (
        <div className="flex flex-col items-center justify-center px-4 py-20 text-center" role="alert">
          <h1 className="text-xl font-semibold text-red-600 mb-2">{app.label} is unavailable</h1>
          <p className="text-gray-700 mb-4">{state.message}</p>
          <button
            type="button"
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:text-red-500"
            onClick={() => setAttempt((n) => n + 1)}
          >
            Try again
          </button>
        </div>
      )}
      <div ref={containerRef} hidden={state.phase !== "ready"} />
    </>
  );
};

export default ChildAppMount;
